import React, { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_CITATION_BY_ID,
  GET_TRANSACTION_IMAGES,
  GetCitationByIdData,
  GetCitationByIdVars,
  GetTransactionImagesData,
  GetTransactionImagesVars,
} from "common/graphql";
import Paper from "@mui/material/Paper";
import { LocalLevelError } from "@app.automotus.io/components/common/LocalLevelError";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ImageCarousel } from "@app.automotus.io/components/ImageCarousel";
import type { CarouselSlide } from "@app.automotus.io/components/ImageCarousel";
import { LoadingSkeleton } from "@app.automotus.io/components/LoadingSkeleton";

export const CitationImageCarousel: React.FC<CitationImageCarouselProps> = ({ citationId }) => {
  const {
    data: citationData,
    loading: citationLoading,
    error: citationError,
    refetch: refetchCitation,
  } = useQuery<GetCitationByIdData, GetCitationByIdVars>(GET_CITATION_BY_ID, {
    variables: { id: citationId },
    onError(err) {
      console.error("failed to load citation", err);
    },
  });

  const [getTransactionImages, { data: imagesData, loading: imagesLoading, error: getImagesError }] = useMutation<
    GetTransactionImagesData,
    GetTransactionImagesVars
  >(GET_TRANSACTION_IMAGES, {
    onError(err) {
      console.error("failed to fetch transaction images", err);
    },
  });

  const transactionId = citationData?.citation?.sessionTransaction?.id || "";

  useEffect(() => {
    if (transactionId !== "") {
      getTransactionImages({ variables: { transactionId } });
    }
  }, [getTransactionImages, transactionId]);

  const error = getImagesError || citationError;
  const loading = imagesLoading || citationLoading;

  const sessionTx = citationData?.citation?.sessionTransaction;
  const slides: CarouselSlide[] = (
    [
      sessionTx?.sessionStartedAtLocal &&
        imagesData?.transactionImages?.licensePlateImageUrl && {
          title: "License Plate Capture",
          date: new Date(sessionTx.sessionStartedAtLocal),
          imageSrc: imagesData.transactionImages.licensePlateImageUrl,
        },
      sessionTx?.sessionStartedAtLocal &&
        imagesData?.transactionImages?.sessionStartImageUrl && {
          title: "Park Start",
          date: new Date(sessionTx.sessionStartedAtLocal),
          imageSrc: imagesData.transactionImages.sessionStartImageUrl,
        },
      sessionTx?.sessionEndedAtLocal &&
        imagesData?.transactionImages?.sessionEndImageUrl && {
          title: "Park End",
          date: new Date(sessionTx.sessionEndedAtLocal),
          imageSrc: imagesData.transactionImages.sessionEndImageUrl,
        },
    ] as Array<CarouselSlide | false | undefined>
  ).filter((v): v is CarouselSlide => Boolean(v));

  return (
    <Paper sx={{ p: 3 }}>
      {error ? (
        <LocalLevelError
          onTryAgain={() => {
            getTransactionImages({ variables: { transactionId } });
            refetchCitation();
          }}
        />
      ) : (
        <Stack spacing={3}>
          <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
            <LoadingSkeleton loading={loading}>
              <Typography variant={"h5"} fontWeight={400}>
                Citation Number: {citationData?.citation?.citationNumber}
              </Typography>
            </LoadingSkeleton>
          </Box>
          <ImageCarousel loading={loading} slides={slides} />
        </Stack>
      )}
    </Paper>
  );
};

export interface CitationImageCarouselProps {
  citationId: string;
}

export default CitationImageCarousel;
